"use client";

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
  type ReactNode,
} from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useLocale, useTranslations } from "next-intl";
import { useRouter } from "@/lib/i18n/navigation";
import { cn } from "@/lib/utils";

// ═══════════════════ Types ═══════════════════

export type FiltersValue = {
  availability?: "today" | "tomorrow" | "thisWeek";
  gender?: "male" | "female";
  conventions: string[];
  languages: string[];
  video: boolean;
  homeVisit: boolean;
  urgentCare: boolean;
  wheelchairAccess: boolean;
  newPatients: boolean;
  priceMax?: number;
  minRating?: number;
};

type BaseProps = {
  initial: FiltersValue;
  baseParams: Record<string, string>;
  totalResults: number;
  className?: string;
};

// ═══════════════════ Constantes ═══════════════════

const AVAILABILITY_KEYS = ["today", "tomorrow", "thisWeek"] as const;

const SERVICE_KEYS = [
  "video",
  "homeVisit",
  "urgentCare",
  "wheelchairAccess",
  "newPatients",
] as const;

const CONVENTION_CODES = ["CNOPS", "CNSS", "AMO", "AMO_TADAMON"] as const;

const LANGUAGE_CODES = ["fr", "ar", "en", "es", "de"] as const;

const PRICES = [250, 350, 500, 800] as const;
const RATINGS = [4.5, 4.0, 3.5] as const;

const LOCALE_BCP47: Record<string, string> = {
  fr: "fr-FR",
  ar: "ar-MA",
  en: "en-GB",
};

const EMPTY_FILTERS: FiltersValue = {
  conventions: [],
  languages: [],
  video: false,
  homeVisit: false,
  urgentCare: false,
  wheelchairAccess: false,
  newPatients: false,
};

// ═══════════════════ Helpers ═══════════════════

function filtersToParams(f: FiltersValue): Record<string, string> {
  const out: Record<string, string> = {};
  if (f.availability) out.availability = f.availability;
  if (f.gender) out.gender = f.gender;
  if (f.conventions.length) out.conventions = f.conventions.join(",");
  if (f.languages.length) out.languages = f.languages.join(",");
  if (f.video) out.video = "1";
  if (f.homeVisit) out.homeVisit = "1";
  if (f.urgentCare) out.urgentCare = "1";
  if (f.wheelchairAccess) out.wheelchairAccess = "1";
  if (f.newPatients) out.newPatients = "1";
  if (f.priceMax) out.priceMax = String(f.priceMax);
  if (f.minRating) out.minRating = String(f.minRating);
  return out;
}

function countActive(f: FiltersValue): number {
  return [
    f.availability,
    f.gender,
    f.conventions.length,
    f.languages.length,
    f.video,
    f.homeVisit,
    f.urgentCare,
    f.wheelchairAccess,
    f.newPatients,
    f.priceMax,
    f.minRating,
  ].filter(Boolean).length;
}

function toggleArr(arr: string[], v: string): string[] {
  return arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];
}

// ═══════════════════ Hook : fetch live count from /api/physicians for current draft ═══════════════════

function useLiveCount(
  draft: FiltersValue,
  baseParams: Record<string, string>,
  fallback: number,
): { count: number; loading: boolean } {
  const [count, setCount] = useState(fallback);
  const [loading, setLoading] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Stable serialization of draft + base params for dependency tracking
  const key = useMemo(
    () => JSON.stringify({ ...baseParams, ...filtersToParams(draft) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(baseParams), JSON.stringify(draft)],
  );

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      if (abortRef.current) abortRef.current.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setLoading(true);
      const params = new URLSearchParams({ ...baseParams, ...filtersToParams(draft), pageSize: "1" });
      fetch(`/api/physicians?${params.toString()}`, { signal: ctrl.signal })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (data && typeof data.total === "number") setCount(data.total);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 250);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return { count, loading };
}

// ═══════════════════ Shared hook : applique filtres via router.push ═══════════════════

function useFiltersApply(baseParams: Record<string, string>) {
  const router = useRouter();
  return useCallback(
    (next: FiltersValue) => {
      const params = new URLSearchParams({
        ...baseParams,
        ...filtersToParams(next),
      });
      router.push(`/recherche?${params.toString()}` as never);
    },
    [baseParams, router],
  );
}

// ═══════════════════ Icons ═══════════════════

function IconFilter({ className }: { className?: string }) {
  return (<svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden><path d="M4 6h16M7 12h10M10 18h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>);
}
function IconClose({ className }: { className?: string }) {
  return (<svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden><path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/></svg>);
}
function IconReset({ className }: { className?: string }) {
  return (<svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden><path d="M3 12a9 9 0 1 0 3-6.7L3 8M3 3v5h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>);
}

// ═══════════════════ Body (shared mobile + desktop) ═══════════════════

function FiltersBody({
  draft,
  setDraft,
}: {
  draft: FiltersValue;
  setDraft: (v: FiltersValue) => void;
}) {
  const t = useTranslations("search.filtersPanel");
  const tLang = useTranslations("search.languages");
  return (
    <div className="min-w-0 space-y-6">
      <FilterSection title={t("sections.availability")}>
        <Pills>
          {AVAILABILITY_KEYS.map((key) => (
            <ChipFlat
              key={key}
              active={draft.availability === key}
              onClick={() =>
                setDraft({
                  ...draft,
                  availability: draft.availability === key ? undefined : key,
                })
              }
            >
              {t(`availabilityOptions.${key}`)}
            </ChipFlat>
          ))}
        </Pills>
      </FilterSection>

      <FilterSection title={t("sections.services")}>
        <Pills>
          {SERVICE_KEYS.map((key) => {
            const active = Boolean(draft[key]);
            return (
              <ChipFlat
                key={key}
                active={active}
                onClick={() => setDraft({ ...draft, [key]: !active })}
              >
                {t(`serviceOptions.${key}`)}
              </ChipFlat>
            );
          })}
        </Pills>
      </FilterSection>

      <FilterSection title={t("sections.conventions")}>
        <Pills>
          {CONVENTION_CODES.map((code) => (
            <ChipFlat
              key={code}
              active={draft.conventions.includes(code)}
              onClick={() => setDraft({ ...draft, conventions: toggleArr(draft.conventions, code) })}
            >
              {code === "AMO_TADAMON" ? "AMO Tadamon" : code}
            </ChipFlat>
          ))}
        </Pills>
      </FilterSection>

      <FilterSection title={t("sections.languages")}>
        <Pills>
          {LANGUAGE_CODES.map((code) => (
            <ChipFlat
              key={code}
              active={draft.languages.includes(code)}
              onClick={() =>
                setDraft({ ...draft, languages: toggleArr(draft.languages, code) })
              }
            >
              {tLang(code)}
            </ChipFlat>
          ))}
        </Pills>
      </FilterSection>

      <FilterSection title={t("sections.priceMax")}>
        <Pills>
          {PRICES.map((value) => (
            <ChipFlat
              key={value}
              active={draft.priceMax === value}
              onClick={() =>
                setDraft({ ...draft, priceMax: draft.priceMax === value ? undefined : value })
              }
            >
              {t("priceLte", { value })}
            </ChipFlat>
          ))}
        </Pills>
      </FilterSection>

      <FilterSection title={t("sections.minRating")}>
        <Pills>
          {RATINGS.map((value) => (
            <ChipFlat
              key={value}
              active={draft.minRating === value}
              onClick={() =>
                setDraft({ ...draft, minRating: draft.minRating === value ? undefined : value })
              }
            >
              {t("ratingPlus", { value })}
            </ChipFlat>
          ))}
        </Pills>
      </FilterSection>

      <FilterSection title={t("sections.gender")}>
        <Pills>
          <ChipFlat active={draft.gender === "female"} onClick={() => setDraft({ ...draft, gender: draft.gender === "female" ? undefined : "female" })}>
            {t("female")}
          </ChipFlat>
          <ChipFlat active={draft.gender === "male"} onClick={() => setDraft({ ...draft, gender: draft.gender === "male" ? undefined : "male" })}>
            {t("male")}
          </ChipFlat>
        </Pills>
      </FilterSection>
    </div>
  );
}

// Native-app style flat chip: white bg + gray border, active = primary[50] bg + check + primary border
function ChipFlat({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 py-2 text-[13.5px] font-semibold transition-all active:scale-[0.97]",
        active
          ? "border-[var(--sahha-primary-600)] bg-[var(--sahha-primary-50)] text-[var(--sahha-primary-800)]"
          : "border-[var(--sahha-border-default)] bg-white text-[var(--sahha-text-700)] hover:border-[var(--sahha-primary-300)]",
      )}
    >
      {active && (
        <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" aria-hidden>
          <path d="M3 8l3.5 3.5L13 5" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
      {children}
    </button>
  );
}

// ═══════════════════ MOBILE — quick chips + bottom-sheet drawer ═══════════════════

export function SearchFiltersMobile({
  initial,
  baseParams,
  totalResults,
  className,
}: BaseProps) {
  const t = useTranslations("search.filtersPanel");
  const locale = useLocale();
  const numLocale = LOCALE_BCP47[locale] ?? "en-GB";
  const apply = useFiltersApply(baseParams);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<FiltersValue>(initial);

  // Resync draft si les searchParams changent (navigation entrante)
  useEffect(() => {
    setDraft(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(initial)]);

  // Quand le drawer ouvre/ferme, toggle la classe body.filters-drawer-open
  // pour masquer la BottomNav — scroll-lock est géré par Radix Dialog.
  useEffect(() => {
    if (open) {
      document.body.classList.add("filters-drawer-open");
    } else {
      document.body.classList.remove("filters-drawer-open");
    }
    return () => {
      document.body.classList.remove("filters-drawer-open");
    };
  }, [open]);

  const activeCount = useMemo(() => countActive(draft), [draft]);
  const initialActive = useMemo(() => countActive(initial), [initial]);
  const { count: liveCount, loading: countLoading } = useLiveCount(draft, baseParams, totalResults);

  function reset() {
    setDraft(EMPTY_FILTERS);
    apply(EMPTY_FILTERS);
  }

  function applyAndClose() {
    apply(draft);
    setOpen(false);
  }

  const quickToggle = {
    availability: (key: "today" | "tomorrow" | "thisWeek") =>
      apply({ ...initial, availability: initial.availability === key ? undefined : key }),
    video: () => apply({ ...initial, video: !initial.video }),
    homeVisit: () => apply({ ...initial, homeVisit: !initial.homeVisit }),
    urgentCare: () => apply({ ...initial, urgentCare: !initial.urgentCare }),
    minRating: (v: number) => apply({ ...initial, minRating: initial.minRating === v ? undefined : v }),
    convention: (code: string) =>
      apply({ ...initial, conventions: toggleArr(initial.conventions, code) }),
  };

  return (
    <>
      {/* Quick filter row — horizontal scroll, chips style Doctolib */}
      <div className={cn("min-w-0", className)}>
        <div className="-mx-4 flex items-stretch gap-2 overflow-x-auto scroll-smooth px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:-mx-6 sm:px-6">
          <Dialog.Root open={open} onOpenChange={setOpen}>
            <Dialog.Trigger asChild>
              <button
                type="button"
                className="group relative inline-flex h-10 shrink-0 items-center gap-2 rounded-full border border-[var(--sahha-border-strong)] bg-white px-3.5 text-[13px] font-semibold text-[var(--sahha-text-800)] shadow-[0_2px_8px_-2px_rgba(15,30,58,0.1)] transition-all hover:border-[var(--sahha-primary-400)] active:scale-[0.97]"
              >
                <IconFilter className="h-4 w-4 text-[var(--sahha-primary-700)]" />
                <span>{t("triggerMobile")}</span>
                {initialActive > 0 && (
                  <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[var(--sahha-primary-600)] px-1.5 text-[11px] font-bold text-white">
                    {initialActive}
                  </span>
                )}
              </button>
            </Dialog.Trigger>

            <Dialog.Portal>
              <Dialog.Overlay
                className={cn(
                  "fixed inset-0 z-[60] bg-[rgb(15_30_58_/_0.6)] backdrop-blur-sm",
                  "data-[state=open]:animate-in data-[state=closed]:animate-out",
                  "data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
                )}
              />
              <Dialog.Content
                aria-describedby={undefined}
                onOpenAutoFocus={(e) => {
                  // Laisse Radix gérer le focus trap mais ne scroll pas automatiquement
                  e.preventDefault();
                }}
                className={cn(
                  "fixed inset-x-0 bottom-0 z-[70] flex flex-col overflow-hidden rounded-t-[28px] bg-white",
                  "max-h-[92dvh] shadow-[0_-24px_60px_-12px_rgba(15,30,58,0.35)]",
                  "data-[state=open]:animate-in data-[state=closed]:animate-out",
                  "data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom",
                  "data-[state=open]:duration-300 data-[state=closed]:duration-200",
                  "focus:outline-none",
                )}
              >
                {/* Grab handle */}
                <div className="flex shrink-0 justify-center pt-2.5 pb-1">
                  <div className="h-1 w-10 rounded-full bg-[var(--sahha-border-strong)]" />
                </div>

                <header className="flex shrink-0 items-center justify-between px-5 py-3">
                  <div>
                    <Dialog.Title className="text-lg font-semibold text-[var(--sahha-text-900)]">
                      {t("titleMobile")}
                    </Dialog.Title>
                    <p className="text-xs text-[var(--sahha-text-500)]">
                      {activeCount > 0
                        ? t("activeMobile", { count: activeCount })
                        : t("subtitleEmpty")}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    {activeCount > 0 && (
                      <button
                        type="button"
                        onClick={reset}
                        className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium text-[var(--sahha-primary-700)] hover:bg-[var(--sahha-primary-50)] active:scale-[0.97]"
                      >
                        <IconReset className="h-3.5 w-3.5" />
                        {t("resetMobile")}
                      </button>
                    )}
                    <Dialog.Close asChild>
                      <button
                        type="button"
                        aria-label={t("closeAria")}
                        className="inline-flex h-10 w-10 items-center justify-center rounded-full text-[var(--sahha-text-600)] hover:bg-[var(--sahha-surface-1)] active:scale-95"
                      >
                        <IconClose className="h-5 w-5" />
                      </button>
                    </Dialog.Close>
                  </div>
                </header>

                <div className="flex-1 overflow-y-auto overscroll-contain px-5 pb-4">
                  <FiltersBody draft={draft} setDraft={setDraft} />
                </div>

                {/* Sticky footer CTA — safe-area bottom pour home indicator iOS */}
                <footer className="shrink-0 border-t border-[var(--sahha-border-subtle)] bg-white/95 px-4 pt-3 backdrop-blur-md pb-[max(env(safe-area-inset-bottom),1rem)]">
                  <button
                    type="button"
                    onClick={applyAndClose}
                    disabled={liveCount === 0}
                    className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[var(--sahha-primary-600)] text-[15px] font-semibold text-white shadow-[0_4px_12px_-2px_rgba(21,112,239,0.35)] transition-all hover:bg-[var(--sahha-primary-700)] active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <span>{t("apply")}</span>
                    {!countLoading && (
                      <span className="inline-flex items-center justify-center rounded-full bg-white/20 px-2 py-0.5 text-[12.5px] font-bold tabular-nums">
                        {liveCount.toLocaleString(numLocale)}
                      </span>
                    )}
                    {countLoading && (
                      <span aria-hidden className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                    )}
                  </button>
                </footer>
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>

          <div aria-hidden className="shrink-0 self-stretch w-px bg-[var(--sahha-border-default)]" />

          <QuickPill active={initial.availability === "today"} onClick={() => quickToggle.availability("today")}>
            <span aria-hidden>🔥</span> {t("quick.today")}
          </QuickPill>
          <QuickPill active={initial.video} onClick={quickToggle.video}>
            <span aria-hidden>📹</span> {t("quick.video")}
          </QuickPill>
          <QuickPill active={initial.minRating === 4.5} onClick={() => quickToggle.minRating(4.5)}>
            <span aria-hidden>⭐</span> {t("quick.rating45")}
          </QuickPill>
          <QuickPill active={initial.conventions.includes("CNSS")} onClick={() => quickToggle.convention("CNSS")}>
            <span aria-hidden>💳</span> {t("quick.cnss")}
          </QuickPill>
          <QuickPill active={initial.conventions.includes("CNOPS")} onClick={() => quickToggle.convention("CNOPS")}>
            <span aria-hidden>🏛️</span> {t("quick.cnops")}
          </QuickPill>
          <QuickPill active={initial.conventions.includes("AMO")} onClick={() => quickToggle.convention("AMO")}>
            <span aria-hidden>🛡️</span> {t("quick.amo")}
          </QuickPill>
          <QuickPill active={initial.homeVisit} onClick={quickToggle.homeVisit}>
            <span aria-hidden>🏠</span> {t("quick.homeVisit")}
          </QuickPill>
          <QuickPill active={initial.availability === "thisWeek"} onClick={() => quickToggle.availability("thisWeek")}>
            <span aria-hidden>🗓️</span> {t("quick.thisWeek")}
          </QuickPill>
          <QuickPill active={initial.urgentCare} onClick={quickToggle.urgentCare}>
            <span aria-hidden>🚑</span> {t("quick.urgentCare")}
          </QuickPill>
        </div>
      </div>
    </>
  );
}

// ═══════════════════ DESKTOP — toolbar + centered modal popup ═══════════════════

export function SearchFiltersDesktop({
  initial,
  baseParams,
  totalResults,
  className,
}: BaseProps) {
  const t = useTranslations("search.filtersPanel");
  const locale = useLocale();
  const numLocale = LOCALE_BCP47[locale] ?? "en-GB";
  const apply = useFiltersApply(baseParams);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<FiltersValue>(initial);

  useEffect(() => {
    setDraft(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(initial)]);

  const activeCount = useMemo(() => countActive(draft), [draft]);
  const initialActive = useMemo(() => countActive(initial), [initial]);
  const { count: liveCountDesktop, loading: countLoadingDesktop } = useLiveCount(draft, baseParams, totalResults);

  function reset() {
    setDraft(EMPTY_FILTERS);
    apply(EMPTY_FILTERS);
  }

  function applyAndClose() {
    apply(draft);
    setOpen(false);
  }

  // Quick apply (no modal) for the header pills
  const quickToggle = {
    availability: (key: "today" | "tomorrow" | "thisWeek") =>
      apply({ ...initial, availability: initial.availability === key ? undefined : key }),
    video: () => apply({ ...initial, video: !initial.video }),
    convention: (code: string) =>
      apply({ ...initial, conventions: toggleArr(initial.conventions, code) }),
  };

  return (
    <div className={cn("hidden lg:block", className)}>
      <div className="flex flex-nowrap items-center gap-2">
        {/* Main "Filtres" button → centered modal popup */}
        <Dialog.Root open={open} onOpenChange={setOpen}>
          <Dialog.Trigger asChild>
            <button
              type="button"
              className="inline-flex h-10 items-center gap-2 rounded-full border border-[var(--sahha-border-strong)] bg-white px-4 text-[13.5px] font-semibold text-[var(--sahha-text-800)] shadow-[0_2px_8px_-2px_rgba(15,30,58,0.08)] transition-all hover:border-[var(--sahha-primary-400)] hover:bg-[var(--sahha-primary-50)] active:scale-[0.98]"
            >
              <IconFilter className="h-4 w-4 text-[var(--sahha-primary-700)]" />
              <span>{t("triggerDesktop")}</span>
              {initialActive > 0 && (
                <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[var(--sahha-primary-600)] px-1.5 text-[11px] font-bold text-white">
                  {initialActive}
                </span>
              )}
            </button>
          </Dialog.Trigger>

          <Dialog.Portal>
            <Dialog.Overlay
              className={cn(
                "fixed inset-0 z-[60] bg-[rgb(15_30_58_/_0.55)] backdrop-blur-sm",
                "data-[state=open]:animate-in data-[state=closed]:animate-out",
                "data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
              )}
            />
            <Dialog.Content
              aria-describedby={undefined}
              className={cn(
                "fixed left-1/2 top-1/2 z-[70] w-[min(92vw,720px)] -translate-x-1/2 -translate-y-1/2",
                "flex max-h-[88vh] flex-col overflow-hidden rounded-3xl bg-white",
                "shadow-[0_30px_80px_-20px_rgba(15,30,58,0.4)]",
                "data-[state=open]:animate-in data-[state=closed]:animate-out",
                "data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
                "data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95",
                "data-[state=open]:duration-200 focus:outline-none",
              )}
            >
              <header className="flex shrink-0 items-center justify-between gap-3 border-b border-[var(--sahha-border-subtle)] px-6 py-4">
                <div className="min-w-0">
                  <Dialog.Title className="text-lg font-semibold text-[var(--sahha-text-900)]">
                    {t("titleDesktop")}
                  </Dialog.Title>
                  <p className="mt-0.5 text-xs text-[var(--sahha-text-500)]">
                    {activeCount > 0
                      ? t("activeDesktop", { count: activeCount })
                      : t("subtitleEmpty")}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  {activeCount > 0 && (
                    <button
                      type="button"
                      onClick={reset}
                      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium text-[var(--sahha-primary-700)] hover:bg-[var(--sahha-primary-50)]"
                    >
                      <IconReset className="h-3.5 w-3.5" />
                      {t("resetDesktop")}
                    </button>
                  )}
                  <Dialog.Close asChild>
                    <button
                      type="button"
                      aria-label={t("closeAriaShort")}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-full text-[var(--sahha-text-600)] hover:bg-[var(--sahha-surface-1)] active:scale-95"
                    >
                      <IconClose className="h-5 w-5" />
                    </button>
                  </Dialog.Close>
                </div>
              </header>

              <div className="flex-1 overflow-y-auto px-6 py-5">
                <FiltersBody draft={draft} setDraft={setDraft} />
              </div>

              <footer className="flex shrink-0 items-center justify-between gap-3 border-t border-[var(--sahha-border-subtle)] bg-[var(--sahha-surface-0)]/60 px-6 py-3">
                <Dialog.Close asChild>
                  <button
                    type="button"
                    className="inline-flex h-10 items-center gap-1.5 rounded-full px-4 text-[13px] font-medium text-[var(--sahha-text-700)] hover:bg-[var(--sahha-surface-1)]"
                  >
                    {t("cancel")}
                  </button>
                </Dialog.Close>
                <button
                  type="button"
                  onClick={applyAndClose}
                  disabled={liveCountDesktop === 0}
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-[var(--sahha-primary-600)] px-6 text-[14px] font-semibold text-white shadow-[0_4px_12px_-2px_rgba(21,112,239,0.35)] transition-all hover:bg-[var(--sahha-primary-700)] active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <span>{t("apply")}</span>
                  {!countLoadingDesktop && (
                    <span className="inline-flex items-center justify-center rounded-full bg-white/20 px-2 py-0.5 text-[12px] font-bold tabular-nums">
                      {liveCountDesktop.toLocaleString(numLocale)}
                    </span>
                  )}
                  {countLoadingDesktop && (
                    <span aria-hidden className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                  )}
                </button>
              </footer>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>

        {/* Quick desktop chips — instant apply */}
        <span aria-hidden className="h-6 w-px bg-[var(--sahha-border-default)]" />
        <QuickPill active={initial.availability === "today"} onClick={() => quickToggle.availability("today")}>
          <span aria-hidden>🔥</span> {t("quick.today")}
        </QuickPill>
        <QuickPill active={initial.availability === "thisWeek"} onClick={() => quickToggle.availability("thisWeek")}>
          <span aria-hidden>🗓️</span> {t("quick.thisWeek")}
        </QuickPill>
        <QuickPill active={initial.video} onClick={quickToggle.video}>
          <span aria-hidden>📹</span> {t("quick.videoLong")}
        </QuickPill>
        <QuickPill active={initial.conventions.includes("CNSS")} onClick={() => quickToggle.convention("CNSS")}>
          <span aria-hidden>💳</span> {t("quick.cnss")}
        </QuickPill>
        <QuickPill active={initial.conventions.includes("CNOPS")} onClick={() => quickToggle.convention("CNOPS")}>
          <span aria-hidden>🏛️</span> {t("quick.cnops")}
        </QuickPill>
      </div>
    </div>
  );
}

// ═══════════════════ Sous-composants UI ═══════════════════

function FilterSection({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <fieldset className="min-w-0">
      <legend className="mb-2 block w-full">
        <span className="block text-[13px] font-semibold leading-tight text-[var(--sahha-text-900)]">{title}</span>
        {hint && <span className="mt-0.5 block text-[11px] leading-tight text-[var(--sahha-text-500)]">{hint}</span>}
      </legend>
      {children}
    </fieldset>
  );
}

function Pills({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap gap-1.5">{children}</div>;
}

function QuickPill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "inline-flex h-10 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 text-[13px] font-medium leading-none transition-all duration-150 active:scale-[0.97]",
        active
          ? "bg-[var(--sahha-primary-600)] text-white shadow-[0_4px_12px_-2px_rgba(21,112,239,0.4)]"
          : "border border-[var(--sahha-border-default)] bg-white text-[var(--sahha-text-700)] hover:border-[var(--sahha-primary-300)] hover:bg-[var(--sahha-primary-50)]",
      )}
    >
      {children}
    </button>
  );
}

// ═══════════════════ Rétro-compat : ancien export par défaut ═══════════════════
// @deprecated — utilisez <SearchFiltersMobile /> + <SearchFiltersDesktop /> séparément
// pour éviter le double rendu sur mobile.
export function SearchFilters(props: BaseProps) {
  return (
    <>
      <div className="lg:hidden">
        <SearchFiltersMobile {...props} />
      </div>
      <SearchFiltersDesktop {...props} />
    </>
  );
}
